import React, { useEffect, useRef, useState } from 'react';
import { resolveAvatarUrl } from '../../utils/media';
import { ColorModeIcon, useColorMode } from '../ui/color-mode';

const MENU_CLOSE_DELAY_MS = 2000;

function userInitial(userName) {
  if (!userName || !String(userName).trim()) return '';
  return String(userName).trim().slice(0, 1).toUpperCase();
}

function AppsIcon() {
  return (
    <svg
      className="header-apps-icon"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="currentColor">
        <circle cx="5" cy="5" r="1.6" />
        <circle cx="12" cy="5" r="1.6" />
        <circle cx="19" cy="5" r="1.6" />
        <circle cx="5" cy="12" r="1.6" />
        <circle cx="12" cy="12" r="1.6" />
        <circle cx="19" cy="12" r="1.6" />
        <circle cx="5" cy="19" r="1.6" />
        <circle cx="12" cy="19" r="1.6" />
        <circle cx="19" cy="19" r="1.6" />
      </g>
    </svg>
  );
}

const NAV_LINKS = [
  { en: 'Life Guide', zh: '人生指南', paths: ['/', '/page/aevdir'], navigate: 'onNavigateAevdir' },
  { en: 'Happy Dog', zh: '快乐小狗', paths: ['/page/happy-dog'], navigate: 'onNavigateHappyDog' },
];

function Header({
  user,
  onLogin,
  onRegister,
  onLogout,
  lang,
  onToggleLang,
  onNavigateSupport,
  onNavigateHome,
  onNavigatePrivacy,
  onNavigateProfile,
  onNavigateClock,
  onNavigateAevdir,
  onNavigateHappyDog,
  currentPath,
}) {
  const isEn = lang === 'en';
  const { toggleColorMode } = useColorMode();
  const activePath = currentPath || '/';
  const langHoverTitle = isEn ? 'Switch to Chinese' : '切换为英语';
  const [menuOpen, setMenuOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const appsCloseTimerRef = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleCloseMenu = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setMenuOpen(false);
      closeTimerRef.current = null;
    }, MENU_CLOSE_DELAY_MS);
  };

  const handleMenuWrapEnter = () => {
    clearCloseTimer();
    closeAppsMenu();
    setMenuOpen(true);
  };

  const handleMenuWrapLeave = () => {
    scheduleCloseMenu();
  };

  const clearAppsCloseTimer = () => {
    if (appsCloseTimerRef.current != null) {
      clearTimeout(appsCloseTimerRef.current);
      appsCloseTimerRef.current = null;
    }
  };

  const scheduleCloseAppsMenu = () => {
    clearAppsCloseTimer();
    appsCloseTimerRef.current = setTimeout(() => {
      setAppsOpen(false);
      appsCloseTimerRef.current = null;
    }, MENU_CLOSE_DELAY_MS);
  };

  const closeUserMenu = () => {
    setMenuOpen(false);
    clearCloseTimer();
  };

  const closeAppsMenu = () => {
    setAppsOpen(false);
    clearAppsCloseTimer();
  };

  const handleAppsWrapEnter = () => {
    clearAppsCloseTimer();
    closeUserMenu();
    setAppsOpen(true);
  };

  const handleAppsWrapLeave = () => {
    scheduleCloseAppsMenu();
  };

  const handleAppsButtonClick = () => {
    clearAppsCloseTimer();
    closeUserMenu();
    setAppsOpen((prev) => !prev);
  };

  useEffect(() => {
    return () => {
      clearCloseTimer();
      clearAppsCloseTimer();
    };
  }, []);

  const handleSupportClick = () => {
    setMenuOpen(false);
    clearCloseTimer();
    if (typeof onNavigateSupport === 'function') onNavigateSupport();
  };

  const handlePrivacyClick = () => {
    setMenuOpen(false);
    clearCloseTimer();
    if (typeof onNavigatePrivacy === 'function') onNavigatePrivacy();
  };

  const handleProfileClick = () => {
    setMenuOpen(false);
    clearCloseTimer();
    if (typeof onNavigateProfile === 'function') onNavigateProfile();
  };

  const handleClockAppClick = () => {
    setAppsOpen(false);
    clearAppsCloseTimer();
    if (typeof onNavigateClock === 'function') onNavigateClock();
  };

  const handleBrandClick = () => {
    if (typeof onNavigateHome === 'function') onNavigateHome();
  };

  const navHandlers = { onNavigateAevdir, onNavigateHappyDog };

  return (
    <header className="app-header" aria-label={isEn ? 'Site header' : '站点页眉'}>
      <button
        type="button"
        className="app-brand-btn"
        onClick={handleBrandClick}
        aria-label={isEn ? 'Go to home' : '回到首页'}
      >
        <span className="app-brand-mark" aria-hidden="true">
          <img
            className="app-brand-logo-icon"
            src="/aevdir-logo-icon.png"
            alt=""
            width="70"
            height="34"
            loading="eager"
            decoding="async"
          />
          <img
            className="app-brand-logo-text"
            src="/aevdir-logo-text.png"
            alt=""
            width="97"
            height="24"
            loading="eager"
            decoding="async"
          />
        </span>
      </button>

      <nav className="header-nav" aria-label={isEn ? 'Main navigation' : '主导航'}>
        {NAV_LINKS.map((link) => {
          const isActive = link.paths.includes(activePath);
          const handler = navHandlers[link.navigate];
          return (
            <button
              key={link.en}
              type="button"
              className={`header-nav-link${isActive ? ' is-active' : ''}`}
              onClick={() => typeof handler === 'function' && handler()}
            >
              {isEn ? link.en : link.zh}
            </button>
          );
        })}
      </nav>

      <div className="header-actions">
        <button
          type="button"
          className="btn-secondary lang-switch"
          onClick={toggleColorMode}
          title={isEn ? 'Toggle dark mode' : '切换深色模式'}
          aria-label={isEn ? 'Toggle dark mode' : '切换深色模式'}
        >
          <ColorModeIcon />
        </button>
        <button
          type="button"
          className="btn-secondary lang-switch"
          onClick={onToggleLang}
          title={langHoverTitle}
        >
          {isEn ? 'E' : '中'}
        </button>
        <div
          className="header-apps-wrap"
          onMouseEnter={handleAppsWrapEnter}
          onMouseLeave={handleAppsWrapLeave}
        >
          <button
            type="button"
            className="header-apps-btn"
            onClick={handleAppsButtonClick}
            aria-label={isEn ? 'Apps' : '应用'}
            aria-haspopup="true"
            aria-expanded={appsOpen}
            title={isEn ? 'Apps' : '应用'}
          >
            <AppsIcon />
          </button>
          <div className={`header-apps-flyout${appsOpen ? ' is-open' : ''}`}>
            <div className="header-apps-panel" role="menu">
              <div className="header-apps-panel-title">
                {isEn ? 'Apps' : '应用'}
              </div>
              <div className="header-apps-grid">
                <button
                  type="button"
                  className="header-app-tile"
                  onClick={handleClockAppClick}
                  role="menuitem"
                  title={isEn ? 'Wake Up Duck' : '醒醒鸭'}
                >
                  <span className="header-app-tile-icon">
                    <img
                      src="/clock-icon.png"
                      alt=""
                      width="40"
                      height="40"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="header-app-tile-label">
                    {isEn ? 'Wake Up Duck' : '醒醒鸭'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          className="header-user-wrap"
          onMouseEnter={handleMenuWrapEnter}
          onMouseLeave={handleMenuWrapLeave}
        >
          <button
            type="button"
            className="header-avatar"
            aria-label={user ? (isEn ? 'Account menu' : '账户菜单') : isEn ? 'Guest menu' : '游客菜单'}
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            {user ? (
              resolveAvatarUrl(user.avatarUrl) ? (
                <img
                  src={resolveAvatarUrl(user.avatarUrl)}
                  alt=""
                  className="header-avatar-img"
                />
              ) : (
                <span className="header-avatar-letter">{userInitial(user.userName)}</span>
              )
            ) : null}
          </button>
          <div className={`header-user-flyout${menuOpen ? ' is-open' : ''}`}>
            <div className="header-user-dropdown" role="menu">
              {user ? (
                <>
                  <div className="header-dropdown-user" role="presentation">
                    <span className="header-dropdown-name">{user.userName}</span>
                    {user.email && !user.emailVerified ? (
                      <span className="header-email-badge">
                        {isEn ? 'Email not verified' : '邮箱未验证'}
                      </span>
                    ) : null}
                  </div>
                  <button type="button" className="header-dropdown-item" onClick={handleProfileClick} role="menuitem">
                    {isEn ? 'Profile' : '个人信息'}
                  </button>
                  <button type="button" className="header-dropdown-item" onClick={handleSupportClick} role="menuitem">
                    {isEn ? 'Support' : '支持'}
                  </button>
                  <button type="button" className="header-dropdown-item" onClick={handlePrivacyClick} role="menuitem">
                    {isEn ? 'Privacy' : '隐私'}
                  </button>
                  <button type="button" className="header-dropdown-item" onClick={onLogout} role="menuitem">
                    {isEn ? 'Sign out' : '退出登录'}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="header-dropdown-item" onClick={onLogin} role="menuitem">
                    {isEn ? 'Sign in' : '登录'}
                  </button>
                  <button type="button" className="header-dropdown-item" onClick={onRegister} role="menuitem">
                    {isEn ? 'Sign up' : '注册'}
                  </button>
                  <button type="button" className="header-dropdown-item" onClick={handleSupportClick} role="menuitem">
                    {isEn ? 'Support' : '支持'}
                  </button>
                  <button type="button" className="header-dropdown-item" onClick={handlePrivacyClick} role="menuitem">
                    {isEn ? 'Privacy' : '隐私'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
